import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart, Download, Sparkles } from "lucide-react";
import { Panel, StatCard } from "@/components/dashboard-shell";
import { formatINR, sampleDonations, stories, getInstitution } from "@/data/sample";
import { TrendAreaChart, DonutChart, RadialGauge } from "@/components/charts";
import { AICauseMatcher } from "@/components/ai-cause-matcher";
import { AIImpactReportCard } from "@/components/ai-impact-report";
import { AITaxAdvisor } from "@/components/ai-tax-advisor";

export const Route = createFileRoute("/donor/")({ component: DonorDash });

function DonorDash() {
  const my = sampleDonations.slice(0, 4);
  const total = my.reduce((s, d) => s + d.amount, 0);
  const inr = (v: number) => formatINR(v);

  // Monthly giving trend (last 6 months)
  const monthly = ["Feb", "Mar", "Apr", "May", "Jun", "Jul"].map((m, i) => ({
    label: m,
    value: [3200, 5100, 4200, 8100, 6400, 9800][i],
  }));

  // Impact allocation
  const allocation = [
    { name: "Fodder & shelter", value: 42 },
    { name: "Medical care", value: 28 },
    { name: "Rescue ops", value: 18 },
    { name: "Rehabilitation", value: 12 },
  ];

  const goalPct = 68;
  return (
    <>
      <div className="mb-7 grid gap-6 rounded-3xl bg-white border border-border/80 p-6 sm:p-8 shadow-xs lg:grid-cols-[1.6fr_1fr] items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--saffron)]/20 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[var(--maroon-deep)] border border-[var(--saffron-deep)]/30">
            <Sparkles className="h-3.5 w-3.5 text-[var(--saffron-deep)]" /> Donor Console
          </span>
          <h1 className="mt-3 font-display text-2xl font-extrabold sm:text-3xl text-[var(--maroon-deep)]">
            Namaste, Rahul 🙏
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground/70 leading-relaxed">
            Your generosity has funded fodder for 84 rescued cows, surgery for 6 stray dogs, and
            rehabilitation for 12 injured birds this year.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/causes"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--maroon)] text-white hover:bg-[var(--maroon-deep)] px-5 py-2.5 text-sm font-bold shadow-xs transition"
            >
              <Heart className="h-4 w-4 fill-white/20" /> Give again
            </Link>
            <Link
              to="/donor/history"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-[var(--cream-2)] px-5 py-2.5 text-sm font-semibold text-[var(--maroon-deep)] hover:bg-white transition"
            >
              <Download className="h-4 w-4" /> Download 80G bundle
            </Link>
          </div>
        </div>
        <div className="rounded-2xl bg-[var(--cream-2)] p-5 border border-border/60 space-y-2">
          <p className="text-xs uppercase tracking-wider text-foreground/50 font-bold">
            Lifetime giving
          </p>
          <p className="font-display text-3xl font-extrabold text-[var(--maroon-deep)]">
            {formatINR(total)}
          </p>
          <p className="text-xs text-foreground/70">Ranked in top 8% of donors this year</p>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[var(--saffron)]/20 px-3 py-1 text-xs font-bold text-[var(--maroon-deep)] border border-[var(--saffron-deep)]/30">
            <Sparkles className="h-3.5 w-3.5 text-[var(--saffron-deep)]" /> Diamond Sevak status
          </div>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total donated" value={formatINR(total)} tone="primary" />
        <StatCard label="Donations" value={String(my.length)} />
        <StatCard label="Institutions supported" value="3" tone="good" />
        <StatCard label="Causes supported" value="4" tone="warn" />
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel
            title="Your giving · last 6 months"
            action={<span className="text-xs text-foreground/70">Monthly ₹</span>}
          >
            <TrendAreaChart data={monthly} valueFormatter={inr} color="var(--maroon)" />
          </Panel>
        </div>
        <Panel title="Yearly goal · ₹1,00,000">
          <RadialGauge
            value={goalPct}
            label="Achieved"
            hint={`${formatINR(68000)} of ₹1L`}
            color="var(--saffron-deep)"
          />
          <p className="mt-3 text-center text-xs text-foreground/70">
            Diamond Sevak · top 8% of donors
          </p>
        </Panel>
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Panel title="Impact allocation">
          <DonutChart data={allocation} valueFormatter={(v) => `${v}%`} />
        </Panel>
        <div className="lg:col-span-2 grid gap-4 md:grid-cols-2">
          <Panel
            title="Recent donations"
            action={
              <Link to="/donor/history" className="text-xs text-[var(--maroon)] underline">
                View all
              </Link>
            }
          >
            <ul className="divide-y divide-border text-sm">
              {my.map((d) => (
                <li key={d.id} className="flex justify-between py-3">
                  <div>
                    <p className="font-medium">{d.cause}</p>
                    <p className="text-xs text-foreground/70">
                      {d.institution} · {d.date}
                    </p>
                  </div>
                  <span className="font-semibold text-[var(--maroon)]">{formatINR(d.amount)}</span>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Updates from causes you support">
            <ul className="space-y-4">
              {stories.slice(0, 3).map((s) => (
                <li key={s.id} className="flex gap-3">
                  <img src={s.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                  <div>
                    <p className="text-sm font-medium leading-snug">{s.title}</p>
                    <p className="text-xs text-foreground/70">
                      {getInstitution(s.institutionId)?.name} · {s.date}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>
      {/* ── AI Feature Row ─────────────────────────── */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <AICauseMatcher preferences={["monthly"]} />
        </div>
        <div className="space-y-4">
          <AITaxAdvisor totalDonated={total} />
        </div>
      </div>
      <div className="mt-4">
        <AIImpactReportCard totalDonated={total} donationCount={my.length} institutionCount={3} />
      </div>
    </>
  );
}
